import { HSBToRGB, RGBToHSB, hexToHSL, hexToRgb, hlsToHex } from 'styles/utils';
import { pad } from './utils.misc';

type ColorTransparencyPercent = 10 | 20 | 30 | 40 | 50 | 60 | 70 | 80 | 90;

export function colorTransparency(color: string, percent: ColorTransparencyPercent): string {
    const transparence = {
        10: '1a',
        20: '33',
        30: '4d',
        40: '66',
        50: '80',
        60: '99',
        70: 'b3',
        80: 'cc',
        90: 'e6',
    };

    return `${color}${transparence[percent]}`;
}

export const colorimetry = {
    isValid: isValidHexColor,
    rgb: (r: number, g: number, b: number) => {
        return {
            toHex: () => {
                return {
                    string: () => {
                        return '#' + pad(r.toString(16)) + pad(g.toString(16)) + pad(b.toString(16));
                    },
                };
            },
            toHsb: () => {
                return {
                    values: () => RGBToHSB(r, g, b),
                    hue: () => RGBToHSB(r, g, b)[0],
                    staturation: () => RGBToHSB(r, g, b)[1],
                    brightness: () => RGBToHSB(r, g, b)[2],
                };
            },
        };
    },
    hsl: (hue: number, saturation: number, lightness: number) => {
        return {
            toHex: () => {
                return {
                    values: () => {
                        return hlsToHex(hue, saturation, lightness);
                    },
                    string: () => {
                        const values = hlsToHex(hue, saturation, lightness);
                        return '#' + values[0] + values[1] + values[2];
                    },
                };
            },
        };
    },
    hsb: (hue: number, saturation: number, brightness: number) => {
        return {
            toRgb: () => {
                return {
                    values: () => HSBToRGB(hue, saturation, brightness),
                    red: () => HSBToRGB(hue, saturation, brightness)[0],
                    green: () => HSBToRGB(hue, saturation, brightness)[1],
                    blue: () => HSBToRGB(hue, saturation, brightness)[2],
                };
            },
        };
    },
    hex: (hex: string) => {
        return {
            toRgbaColor: (opacity: number) => {
                return `rgba(${String(hexToRgb(hex).r).padStart(2, '0')},${String(hexToRgb(hex).g).padStart(
                    2,
                    '0',
                )},${String(hexToRgb(hex).b).padStart(2, '0')},${String(opacity)})`;
            },
            toRgbColor: () => {
                return `rgb(${String(hexToRgb(hex).r).padStart(2, '0')},${String(hexToRgb(hex).g).padStart(
                    2,
                    '0',
                )},${String(hexToRgb(hex).b).padStart(2, '0')})`;
            },
            toRgb: () => {
                return {
                    values: () => {
                        return hexToRgb(hex);
                    },
                    red: () => {
                        return hexToRgb(hex).r;
                    },
                    green: () => {
                        return hexToRgb(hex).g;
                    },
                    blue: () => {
                        return hexToRgb(hex).b;
                    },
                };
            },
            toHsl: () => {
                return {
                    values: () => {
                        return hexToHSL(hex);
                    },
                    hue: () => {
                        return hexToHSL(hex)[0];
                    },
                    saturation: () => {
                        return Math.round(hexToHSL(hex)[1]);
                    },
                    limunance: () => {
                        return Math.round(hexToHSL(hex)[2]);
                    },
                    string: () => {
                        const values = hexToHSL(hex);
                        return 'hsl(' + values[0] + ',' + Math.round(values[1]) + '%,' + Math.round(values[2]) + '%)';
                    },
                };
            },
        };
    },
};

export const getContrast50 = (hexcolor: string) => {
    const hex = hexcolor.replace('#', '');
    return parseInt(hex, 16) > 0xffffff / 2 ? 'black' : 'white';
};

export const getContrastYIQ = (hexcolor: string) => {
    const hex = hexcolor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 125 ? 'black' : 'white';
};

export function isValidHexColor(color: string) {
    return String(color).startsWith('#') && String(color).length >= 4 && String(color).length <= 7;
}
